import {
    EUROPACE_PAYMENT_DATES,
    EUROPACE_SHOPWINDOW_CONDITION_USAGES,
    SALARY_CALCULATION_OPTIONS,
} from 'cpool-constants';
import { schemaConditions, schemaId, schemaLoanRequest, schemaVorgangsnummer } from 'cpool-schemas';
import { z } from 'zod';

import { censorLoanRequest, censorLoanRequestApplicant2Only } from '../../../../../utils/censor.utils';
import type { EventSchema } from '../../../../../utils/EventSchema';
import { _Event } from '../../../../_Event';
import { LoanRequest_Created } from '../../../LoanRequest_Created';

export class LoanRequest_EuropaceLoanOffersErrorsResponseReceived extends _Event {
    constructor(payload: EventSchema<LoanRequest_EuropaceLoanOffersErrorsResponseReceived>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return z.object({
            conditions: schemaConditions,
            error: z.optional(z.any()),
            id: schemaId,
            loanRequest: schemaLoanRequest,
            reason: z.array(z.string().min(1)).optional(),
            response: z.unknown(), // the complete response from europace
            userId: z.string().min(1), // der Adviser der die Anfrage stellt
            vorgangsnummer: schemaVorgangsnummer,
        });
    }

    getCensoredSchema() {
        return this.getSchema().transform((val) => ({
            ...val,
            loanRequest: censorLoanRequest(val.loanRequest),
        }));
    }

    getCensoredSchemaApplicant2Only(_applicant2Id: string) {
        return this.getSchema().transform((val) => ({
            ...val,
            loanRequest: censorLoanRequestApplicant2Only(val.loanRequest),
        }));
    }

    static exampleCensoredApplicant2Only(): EventSchema<LoanRequest_EuropaceLoanOffersErrorsResponseReceived> {
        const example = LoanRequest_EuropaceLoanOffersErrorsResponseReceived.example();
        example.loanRequest = censorLoanRequestApplicant2Only(example.loanRequest);
        return example;
    }

    static example(): EventSchema<LoanRequest_EuropaceLoanOffersErrorsResponseReceived> {
        return {
            conditions: {
                commissionRequestInPercent: 2,
                dataSnippets: {
                    applicant1: {
                        excludeApplicant: false,
                        rentFree: false,
                        salary: 1000,
                        salaryMode: SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                        withoutMaintenanceIncome: false,
                        withoutOtherIncome: false,
                        withoutRentIncome: false,
                        withoutSidejob: false,
                    },
                    applicant2: {
                        excludeApplicant: false,
                        rentFree: false,
                        salary: 1000,
                        salaryMode: SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                        withoutMaintenanceIncome: false,
                        withoutOtherIncome: false,
                        withoutRentIncome: false,
                        withoutSidejob: false,
                    },
                },
                financingPurpose: EUROPACE_SHOPWINDOW_CONDITION_USAGES.FAHRZEUGKAUF,
                paymentDate: EUROPACE_PAYMENT_DATES.MONATSENDE,
                requestAmount: 100000,
                residualDebtInsurance: {
                    applicant1: {
                        death: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        unemployment: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        workIncapacity: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                    },
                    applicant2: {
                        death: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        unemployment: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                        workIncapacity: {
                            desired: false,
                            existing: false,
                            important: false,
                        },
                    },
                },
                runtimeInMonths: 120,
                totalFinancedAmount: 100000,
            },
            error: null,
            id: '22097497-e7b6-402a-a65b-6c02c049d030',
            loanRequest: LoanRequest_Created.example(),
            reason: ['reason1'],
            response: {},
            userId: '22097497-e7b6-402a-a65b-6c02c049d067',
            vorgangsnummer: 'KR3D1T',
        };
    }

    static exampleCensored(): z.infer<
        ReturnType<LoanRequest_EuropaceLoanOffersErrorsResponseReceived['getCensoredSchema']>
    > {
        const censoredExample = LoanRequest_EuropaceLoanOffersErrorsResponseReceived.example();
        censoredExample.loanRequest = censorLoanRequest(censoredExample.loanRequest);
        return censoredExample;
    }
}

export type Schema_LoanRequest_EuropaceLoanOffersErrorsResponseReceived =
    EventSchema<LoanRequest_EuropaceLoanOffersErrorsResponseReceived>;
